import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import WPButton from "../../../../packages/WPButton";
import WPHeading from "../../../../packages/WPHeading";
import WPInput from "../../../../packages/WPInput";

import {
  addUserToTable,
  getUserLicenses,
  hasSelectedLicense,
  validateEmail,
} from "../AssignLicenseUtils";
import { SelectedUsersHeader } from "./SelectedUserHeader";
import { SelectedUserRenderer } from "./SelectedUserRenderer";

import {
  EMAIL_VALIDATOR,
  KEYBOARD_KEYS,
} from "../../../../constants/global.constants";

import { userLicenseValidate } from "../../../../network/LicenseApis";

import "./SelectUsersEmail.scss";

const SelectUsersEmail = ({
  selectedLicenseName,
  onUsersChange,
  onInputErrorChange,
  clearUserError,
}) => {
  const { t } = useTranslation();

  const [rowData, setRowData] = useState([]);
  const [userEmail, setUserEmail] = useState("");
  const [inputError, setInputError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const validateUserLicense = useCallback(
    (userData, licenseId) => {
      const licenses = getUserLicenses({ data: userData });
      const alreadyHasLicense = hasSelectedLicense(licenses, licenseId);
      const isBlocked = userData?.[0]?.isBlocked || false;
      const error = alreadyHasLicense
        ? t("licenseManagement.userAlreadyHasLicense")
        : "";
      const warning =
        !error && isBlocked ? t("licenseManagement.blockedUserWarning") : "";

      return { error, warning };
    },
    [t],
  );

  useEffect(() => {
    if (!selectedLicenseName?.value || rowData.length === 0) {
      return;
    }

    setRowData((prev) =>
      prev.map((user) => {
        if (!user.userData) {
          return user;
        }
        const { error, warning } = validateUserLicense(
          user.userData,
          selectedLicenseName.value,
        );
        return { ...user, error, warning };
      }),
    );
  }, [selectedLicenseName?.value, validateUserLicense, rowData.length]);

  useEffect(() => {
    onUsersChange?.(rowData);
  }, [rowData, onUsersChange]);

  useEffect(() => {
    setUserEmail("");
  }, [selectedLicenseName]);

  useEffect(() => {
    setInputError("");
    onInputErrorChange?.("");
  }, [clearUserError]);

  const handleEmailChange = (event) => {
    const { value } = event.target;
    const errorMsg = validateEmail(value, EMAIL_VALIDATOR, t);

    setUserEmail(value);
    setInputError(errorMsg);
    onInputErrorChange?.(errorMsg);
  };

  const handleAddUser = async () => {
    const email = userEmail.trim();
    if (inputError || !email) {
      const errorMsg = inputError || t("licenseManagement.enterOneEmail");
      setInputError(errorMsg);
      onInputErrorChange?.(errorMsg);
      return;
    }

    const alreadyAdded = rowData.some(
      (row) => String(row?.email || "").toLowerCase() === email.toLowerCase(),
    );
    if (alreadyAdded) {
      const duplicateError = t("licenseManagement.duplicateEmail");
      setInputError(duplicateError);
      onInputErrorChange?.(duplicateError);
      return;
    }

    setIsLoading(true);
    setInputError("");
    onInputErrorChange?.("");

    try {
      const response = await userLicenseValidate(email);
      const userData = response?.data || response;
      const { error, warning } = validateUserLicense(
        userData,
        selectedLicenseName?.value,
      );

      addUserToTable(email, error, userData, setRowData, warning);
      setUserEmail("");
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message || t("licenseManagement.userNotFound");
      setInputError(errorMsg);
      onInputErrorChange?.(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === KEYBOARD_KEYS.ENETR || e.key === "Enter") {
      handleAddUser();
    }
  };

  const handleRemoveUser = (index) => {
    setRowData((prev) => prev.filter((_, idx) => idx !== index));
  };

  const isAddUserDisabled = useMemo(
    () => !userEmail || Boolean(inputError) || isLoading,
    [userEmail, inputError, isLoading],
  );

  return (
    <>
      <div className="add-users-email">
        <WPHeading
          text={t("licenseManagement.addUsers")}
          type="default"
          className="add-users-text"
        />
        <div className="add-users-section">
          <WPInput
            id="user-email-input"
            clearIcon
            containerClassName="search-users-input"
            onChange={handleEmailChange}
            placeholder={t("licenseManagement.enterValidEmail")}
            value={userEmail}
            helperText={
              inputError || t("licenseManagement.pressEnterToAddEmail")
            }
            onKeyDown={handleKeyPress}
            isError={Boolean(inputError)}
            externalError={inputError}
            isLoading={isLoading}
          />
          <WPButton
            id="add-user-button"
            customClass="add-user-button"
            variant="outlined"
            onClick={handleAddUser}
            disabled={isAddUserDisabled}
          >
            {t("licenseManagement.addUser")}
          </WPButton>
        </div>
      </div>
      <div className="selected-users-list">
        {rowData.length > 0 && <SelectedUsersHeader />}
        {rowData.map((row, index) => (
          <div key={row.id} className="selected-users-row">
            <SelectedUserRenderer
              item={row}
              index={index}
              onRemove={() => handleRemoveUser(index)}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default SelectUsersEmail;
